import React, { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";

const ROTATING_MESSAGES = [
  "Workspace is loading...",
  "Initializing block engine & audio libraries...",
  "Loading sprites, costumes, and block palettes...",
  "Preparing your interactive coding studio...",
  "Almost ready for launch! 🚀",
];

const ScratchShimmerLoader = ({
  title = "Practido Visual Block Studio",
  subTitle = "Preparing your interactive block studio",
  isLoaded = false,
}) => {
  const [msgIndex, setMsgIndex] = useState(0);
  const [fadeMsg, setFadeMsg] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeMsg(false);
      setTimeout(() => {
        setMsgIndex((prev) => (prev + 1) % ROTATING_MESSAGES.length);
        setFadeMsg(true);
      }, 200);
    }, 2400);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`absolute inset-0 z-40 bg-[#f8fafc] text-slate-800 flex flex-col transition-opacity duration-500 font-sans overflow-hidden select-none ${
        isLoaded ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      {/* 1. Light Header Bar Skeleton */}
      <div className="h-11 bg-white border-b border-slate-200 px-4 flex items-center justify-between gap-4 flex-shrink-0 relative overflow-hidden shadow-2xs">
        {/* Continuous Shimmer Light Sweep */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-200/40 to-transparent animate-shimmer pointer-events-none" />

        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-xl bg-purple-100 border border-purple-300 animate-pulse" />
          <div className="h-4 w-32 bg-slate-200 rounded-lg animate-pulse" />
        </div>

        <div className="flex items-center gap-2">
          <div className="h-5 w-14 bg-emerald-100 rounded-full border border-emerald-300 animate-pulse" />
          <div className="h-5 w-14 bg-red-100 rounded-full border border-red-300 animate-pulse" />
          <div className="h-6 w-20 bg-slate-200 rounded-xl animate-pulse" />
        </div>
      </div>

      {/* 2. Light Full Shimmer Workspace Studio Body */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Shimmer sweep overlay across full workspace */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-300/20 to-transparent animate-shimmer pointer-events-none z-10" />

        {/* Left Block Categories */}
        <div className="w-16 sm:w-20 bg-slate-100/90 border-r border-slate-200 p-2 flex flex-col items-center gap-3 flex-shrink-0">
          {[
            "bg-blue-500",
            "bg-purple-500",
            "bg-pink-500",
            "bg-amber-400",
            "bg-orange-400",
            "bg-cyan-500",
          ].map((bgColor, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full ${bgColor} animate-pulse border border-white/60 shadow-2xs`} />
              <div className="w-10 h-2 bg-slate-300/80 rounded animate-pulse" />
            </div>
          ))}
        </div>

        {/* Block Library Palette Sidebar */}
        <div className="w-48 sm:w-60 bg-white border-r border-slate-200 p-3 hidden sm:flex flex-col gap-3 flex-shrink-0">
          <div className="h-4 w-28 bg-slate-200 rounded animate-pulse mb-1" />
          <div className="h-9 rounded-2xl bg-blue-50 border border-blue-200 animate-pulse w-[85%]" />
          <div className="h-9 rounded-2xl bg-blue-50 border border-blue-200 animate-pulse w-[70%]" />
          <div className="h-4 w-24 bg-slate-200 rounded animate-pulse mt-2 mb-1" />
          <div className="h-9 rounded-2xl bg-purple-50 border border-purple-200 animate-pulse w-[90%]" />
          <div className="h-9 rounded-2xl bg-purple-50 border border-purple-200 animate-pulse w-[65%]" />
          <div className="h-9 rounded-2xl bg-purple-50 border border-purple-200 animate-pulse w-[80%]" />
        </div>

        {/* Middle Main Script Canvas (Light Background) */}
        <div className="flex-1 bg-white p-6 relative flex flex-col justify-between overflow-hidden">
          {/* Shimmering Code Block Stacks on Canvas */}
          <div className="space-y-3 z-0">
            <div className="w-52 h-10 bg-amber-50 border border-amber-200 rounded-2xl animate-pulse shadow-2xs" />
            <div className="w-72 h-12 bg-blue-50 border border-blue-200 rounded-2xl animate-pulse ml-4 shadow-2xs" />
            <div className="w-60 h-10 bg-emerald-50 border border-emerald-200 rounded-2xl animate-pulse ml-4 shadow-2xs" />
            <div className="w-48 h-10 bg-purple-50 border border-purple-200 rounded-2xl animate-pulse ml-4 shadow-2xs" />
          </div>

          {/* Floating Light-Themed Rotating Loading Status Pill */}
          <div className="relative z-20 self-center mb-6">
            <div className="flex items-center gap-3 bg-white/95 border border-purple-200 rounded-full px-6 py-3 shadow-xl shadow-purple-900/10 backdrop-blur-md ring-4 ring-purple-100">
              <div className="w-3 h-3 rounded-full bg-purple-600 animate-ping flex-shrink-0" />
              <p
                className={`text-xs sm:text-sm font-black text-purple-950 tracking-wide transition-all duration-300 ${
                  fadeMsg ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                }`}
              >
                {ROTATING_MESSAGES[msgIndex]}
              </p>
              <Sparkles className="w-4 h-4 text-amber-500 animate-spin flex-shrink-0 ml-1" />
            </div>
          </div>
        </div>

        {/* Right Stage & Sprite List Preview Skeleton (Light Background) */}
        <div className="w-64 sm:w-80 bg-slate-50 border-l border-slate-200 p-3 hidden md:flex flex-col gap-3 flex-shrink-0">
          <div className="w-full h-48 bg-white rounded-2xl border border-slate-200 shadow-xs relative overflow-hidden flex flex-col justify-between p-3">
            <div className="flex justify-between items-center">
              <div className="w-4 h-4 bg-emerald-400 rounded-full animate-pulse" />
              <div className="w-4 h-4 bg-red-400 rounded-full animate-pulse" />
            </div>
            <div className="w-12 h-12 bg-amber-100 border border-amber-300 rounded-2xl mx-auto animate-bounce" />
            <div className="h-3 w-16 bg-slate-200 rounded mx-auto" />
          </div>

          <div className="flex-1 bg-white rounded-2xl p-2.5 flex flex-col gap-2 border border-slate-200 shadow-xs">
            <div className="h-3.5 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="grid grid-cols-3 gap-2 pt-1">
              <div className="h-16 bg-slate-100 rounded-xl border border-slate-200 animate-pulse" />
              <div className="h-16 bg-slate-100 rounded-xl border border-slate-200 animate-pulse" />
              <div className="h-16 bg-slate-100 rounded-xl border border-slate-200 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScratchShimmerLoader;


